import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FiActivity, FiAlertCircle, FiZap, FiSmartphone } from 'react-icons/fi';
import { AiOutlineGoogle } from 'react-icons/ai';
import { MdAccessibility } from 'react-icons/md';
import { API_URL } from '../../../services/api';

interface PerformanceMetric {
  metric_name: string;
  metric_value: number;
  metric_unit: string;
}

interface WebPerformanceResponse {
  success: boolean;
  data: PerformanceMetric[];
  message?: string;
}

interface MetricDisplay {
  name: string;
  value: string;
  icon: React.ComponentType<any>;
  color: string;
  bgColor: string;
  status: 'excellent' | 'good' | 'poor';
}

const WebPerformanceWidget: React.FC = () => {
  const [metrics, setMetrics] = useState<PerformanceMetric[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchWebPerformance();
  }, []);

  const fetchWebPerformance = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('ไม่พบ access token กรุณาเข้าสู่ระบบใหม่');
      }

      console.log('Fetching web performance from API...');

      
      const response = await axios.get<WebPerformanceResponse>(
        `${API_URL}/api/admin/dashboard/web-performance`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      console.log('Web performance response:', response.data);

      // Handle both wrapped and direct array responses
      if (Array.isArray(response.data)) {
        // Direct array response
        setMetrics(response.data);
        console.log('Web performance loaded successfully:', response.data);
      } else if (response.data.success && response.data.data) {
        // Wrapped response
        setMetrics(response.data.data);
        console.log('Web performance loaded successfully:', response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch web performance');
      }
    } catch (err: any) {
      console.error('Error fetching web performance:', err);
      setError(err.response?.data?.message || err.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  const getMetricDisplay = (metric: PerformanceMetric): MetricDisplay => {
    const metricName = metric.metric_name.toLowerCase();
    let name = '';
    let icon: React.ComponentType<any> = FiActivity;
    let color = 'text-blue-600';
    let bgColor = 'bg-blue-100';
    let status: 'excellent' | 'good' | 'poor' = 'good';

    // Determine status based on metric value
    if (metricName === 'page_load_speed') {
      name = 'Page Load Speed';
      icon = FiZap;
      if (metric.metric_value < 2) {
        status = 'excellent';
        color = 'text-green-600';
        bgColor = 'bg-green-100';
      } else if (metric.metric_value < 4) {
        status = 'good';
        color = 'text-yellow-600';
        bgColor = 'bg-yellow-100';
      } else {
        status = 'poor';
        color = 'text-red-600';
        bgColor = 'bg-red-100';
      }
    } else if (metricName === 'seo_score') {
      name = 'SEO Score';
      icon = AiOutlineGoogle;
      if (metric.metric_value >= 90) {
        status = 'excellent';
        color = 'text-green-600';
        bgColor = 'bg-green-100';
      } else if (metric.metric_value >= 70) {
        status = 'good';
        color = 'text-yellow-600';
        bgColor = 'bg-yellow-100';
      } else {
        status = 'poor';
        color = 'text-red-600';
        bgColor = 'bg-red-100';
      }
    } else if (metricName === 'mobile_score') {
      name = 'Mobile Score';
      icon = FiSmartphone;
      if (metric.metric_value >= 90) {
        status = 'excellent';
        color = 'text-green-600';
        bgColor = 'bg-green-100';
      } else if (metric.metric_value >= 70) {
        status = 'good';
        color = 'text-yellow-600';
        bgColor = 'bg-yellow-100';
      } else {
        status = 'poor';
        color = 'text-red-600';
        bgColor = 'bg-red-100';
      }
    } else if (metricName === 'accessibility_score') {
      name = 'Accessibility';
      icon = MdAccessibility;
      if (metric.metric_value >= 90) {
        status = 'excellent';
        color = 'text-green-600';
        bgColor = 'bg-green-100';
      } else if (metric.metric_value >= 70) {
        status = 'good';
        color = 'text-yellow-600';
        bgColor = 'bg-yellow-100';
      } else {
        status = 'poor';
        color = 'text-red-600';
        bgColor = 'bg-red-100';
      }
    }

    // Format value with unit
    const value = metric.metric_unit === 'seconds'
      ? `${metric.metric_value.toFixed(1)}s`
      : metric.metric_unit === 'points'
      ? `${metric.metric_value}/100`
      : metric.metric_value.toString();

    return { name, value, icon, color, bgColor, status };
  };

  // Loading State
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Web Performance</h3>
          <FiActivity className="h-5 w-5 text-purple-600" />
        </div>
        <div className="grid grid-cols-2 gap-4 animate-pulse">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="h-4 bg-gray-200 rounded w-20"></div>
                <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
              </div>
              <div className="h-6 bg-gray-200 rounded w-16"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Web Performance</h3>
          <FiActivity className="h-5 w-5 text-purple-600" />
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <FiAlertCircle className="h-5 w-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-red-900 mb-1">เกิดข้อผิดพลาด</h4>
              <p className="text-sm text-red-700 mb-3">{error}</p>
              <button
                onClick={fetchWebPerformance}
                className="px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
              >
                ลองอีกครั้ง
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty State
  if (metrics.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Web Performance</h3>
          <FiActivity className="h-5 w-5 text-purple-600" />
        </div>
        <div className="text-center py-8">
          <FiActivity className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-sm text-gray-600">ยังไม่มีข้อมูล Performance</p>
        </div>
      </div>
    );
  }

  // Success State - Display Metrics
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Web Performance</h3>
        <FiActivity className="h-5 w-5 text-purple-600" />
      </div>

      <div className="grid grid-cols-2 gap-4">
        {metrics.map((metric, index) => {
          const display = getMetricDisplay(metric);
          const Icon = display.icon;

          return (
            <div
              key={index}
              className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">
                  {display.name}
                </p>
                <div className={`p-2 rounded-full ${display.bgColor}`}>
                  <Icon className={`h-4 w-4 ${display.color}`} />
                </div>
              </div>
              <div className="flex items-baseline">
                <p className={`text-2xl font-bold ${display.color}`}>
                  {display.value}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WebPerformanceWidget;
